import { useEffect, useState, useCallback } from "react";
import { toast } from "sonner";
import api, { formatApiError, fileUrl, downloadFile } from "@/lib/api";
import { Btn, Label, Input } from "@/components/shared";
import { FileText, Upload, Trash2, Download, Image as ImageIcon } from "lucide-react";

const isImage = (name = "") => /\.(png|jpe?g|gif|webp|heic|heif|bmp)$/i.test(name);

export function DocumentManager({ basePath, title = "Documents", hint, canDelete = true, testid = "doc-manager" }) {
  const [docs, setDocs] = useState([]);
  const [label, setLabel] = useState("");
  const [busy, setBusy] = useState(false);

  const load = useCallback(() => {
    api.get(basePath).then((r) => setDocs(r.data)).catch(() => {});
  }, [basePath]);
  useEffect(() => { load(); }, [load]);

  const onFile = async (e) => {
    const file = e.target.files?.[0];
    e.target.value = "";
    if (!file) return;
    setBusy(true);
    try {
      const fd = new FormData();
      fd.append("file", file);
      const up = await api.post("/upload", fd);
      await api.post(basePath, { file_id: up.data.id, filename: file.name, label: label.trim() });
      setLabel("");
      toast.success("Document uploaded");
      load();
    } catch (err) { toast.error(formatApiError(err.response?.data?.detail)); }
    finally { setBusy(false); }
  };

  const del = async (d) => {
    try { await api.delete(`${basePath}/${d.id}`); toast.success("Document removed"); load(); }
    catch (err) { toast.error(formatApiError(err.response?.data?.detail)); }
  };

  return (
    <div data-testid={testid} className="space-y-3">
      <div>
        <div className="font-head font-bold uppercase text-white text-sm tracking-wide flex items-center gap-1.5"><FileText size={15} className="text-[#FF5722]" /> {title}</div>
        {hint && <p className="text-zinc-500 text-xs mt-1">{hint}</p>}
      </div>

      {docs.length === 0 ? (
        <div className="text-zinc-600 font-mono text-xs border-2 border-dashed border-zinc-900 p-3 text-center">No documents yet.</div>
      ) : (
        <div className="space-y-2">
          {docs.map((d) => (
            <div key={d.id} data-testid={`doc-${d.id}`} className="flex items-center gap-3 border border-zinc-800 bg-black/30 p-2">
              <div className="w-9 h-9 flex items-center justify-center bg-zinc-950 border border-zinc-800 text-[#FF5722] shrink-0">
                {isImage(d.filename) ? <ImageIcon size={16} /> : <FileText size={16} />}
              </div>
              <div className="min-w-0 flex-1">
                <div className="text-white text-sm truncate">{d.label || d.filename || "Document"}</div>
                <div className="font-mono text-[10px] text-zinc-500 truncate">{d.filename} · by {d.uploaded_by}{d.uploaded_by_role === "admin" ? " (dispatch)" : ""}</div>
              </div>
              <button onClick={() => downloadFile(fileUrl(d.file_id), d.filename || "document")} className="text-zinc-500 hover:text-[#FF5722] shrink-0" title="Download" data-testid={`doc-download-${d.id}`}><Download size={15} /></button>
              {canDelete && <button onClick={() => del(d)} className="text-red-500 hover:text-red-400 shrink-0" title="Delete" data-testid={`doc-delete-${d.id}`}><Trash2 size={15} /></button>}
            </div>
          ))}
        </div>
      )}

      <div className="border border-zinc-800 p-3 bg-black/30 space-y-2">
        <Label>Add a document (image, PDF, Word, Excel — up to 25MB)</Label>
        <Input data-testid={`${testid}-label`} value={label} onChange={(e) => setLabel(e.target.value)} placeholder="Label (e.g. Insurance, Price List, Counter-Bid)" />
        <label className="inline-flex">
          <input data-testid={`${testid}-input`} type="file" accept="image/*,application/pdf,.doc,.docx,.xls,.xlsx,.csv,.txt,.ppt,.pptx" onChange={onFile} disabled={busy} className="hidden" />
          <span className={`inline-flex items-center gap-2 font-head font-bold uppercase tracking-wider text-sm px-4 py-2 border-2 border-zinc-700 bg-zinc-900 text-white cursor-pointer hover:border-[#FF5722] shadow-[3px_3px_0px_#000] ${busy ? "opacity-50 pointer-events-none" : ""}`}><Upload size={14} /> {busy ? "Uploading…" : "Upload"}</span>
        </label>
      </div>
    </div>
  );
}
